import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { League } from '@shootout-comps/types';

interface FixtureDetailPageProps {
  leagueId: string;
  divisionId: string;
  fixtureId: string;
  onBack: () => void;
  onOpenFrame: (matchId: string) => void;
}

function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Scheduling, lineup-setting and manual result override for one fixture. Once a lineup is set,
// each match row links into FixtureFramePage (wrapping the shared useMatchPlay hook) to score it.
export default function FixtureDetailPage({ leagueId, divisionId, fixtureId, onBack, onOpenFrame }: FixtureDetailPageProps) {
  const { fetchLeague, updateFixture, setLineup, setMatchExcludeFromStats, setFixtureResult, deleteFixture, quickScoreMatch, addRosterPlayer } = useLeague();
  const [league, setLeague] = useState<League | null>(null);
  const [error, setError] = useState('');

  const [pairs, setPairs] = useState<{ home: string; away: string; excludeHome: boolean; excludeAway: boolean }[]>([]);
  const [savingLineup, setSavingLineup] = useState(false);

  // Lets an organiser add a player who's just joined the team, right from the lineup dropdown,
  // instead of having to leave this page to use the Teams roster-management screen.
  const [addingPlayerFor, setAddingPlayerFor] = useState<{ index: number; side: 'home' | 'away' } | null>(null);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [newPlayerHandicap, setNewPlayerHandicap] = useState('');
  const [savingNewPlayer, setSavingNewPlayer] = useState(false);

  const [homeResult, setHomeResult] = useState('');
  const [awayResult, setAwayResult] = useState('');
  const [savingResult, setSavingResult] = useState(false);

  // Quick per-match score entry — an alternative to opening full shot-by-shot live scoring,
  // for organisers who already know the final score of a match.
  const [quickScoreFor, setQuickScoreFor] = useState<string | null>(null);
  const [qScore1, setQScore1] = useState('');
  const [qScore2, setQScore2] = useState('');
  const [qHighBreak1, setQHighBreak1] = useState('');
  const [qHighBreak2, setQHighBreak2] = useState('');
  const [savingQuickScore, setSavingQuickScore] = useState(false);

  const reload = useCallback(() => {
    fetchLeague(leagueId).then(setLeague).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [fetchLeague, leagueId]);

  useEffect(reload, [reload]);

  const division = league?.divisions.find(d => d._id === divisionId);
  const fixture = division?.fixtures.find(f => f._id === fixtureId);
  const homeTeam = division?.teams.find(t => t._id === fixture?.homeTeamId);
  const awayTeam = division?.teams.find(t => t._id === fixture?.awayTeamId);

  useEffect(() => {
    if (!division || !fixture) return;
    if (fixture.frameMatches.length > 0) {
      setPairs(fixture.frameMatches.map(fm => ({
        home: fm.player1?._id ?? '', away: fm.player2?._id ?? '',
        excludeHome: !!fm.excludeHomeFromSeasonStats, excludeAway: !!fm.excludeAwayFromSeasonStats,
      })));
    } else {
      setPairs(Array.from({ length: division.framesPerFixture }, () => ({ home: '', away: '', excludeHome: false, excludeAway: false })));
    }
    if (fixture.homeFrameWins != null) setHomeResult(String(fixture.homeFrameWins));
    if (fixture.awayFrameWins != null) setAwayResult(String(fixture.awayFrameWins));
  }, [fixture, division]);

  const handleScheduleChange = async (field: 'scheduledAt' | 'venue', value: string) => {
    try {
      const updated = await updateFixture(leagueId, divisionId, fixtureId, {
        [field]: field === 'scheduledAt' ? (value ? new Date(value).toISOString() : null) : (value || null),
      });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    }
  };

  const lineupLocked = !!fixture && fixture.frameMatches.some(fm => fm.status !== 'pending');
  const lineupComplete = pairs.length > 0 && pairs.every(p => p.home && p.away);

  const handleSaveLineup = async () => {
    if (!lineupComplete) return;
    setSavingLineup(true); setError('');
    try {
      const updated = await setLineup(leagueId, divisionId, fixtureId, pairs.map(p => ({ homePlayerId: p.home, awayPlayerId: p.away, excludeHomeFromSeasonStats: p.excludeHome, excludeAwayFromSeasonStats: p.excludeAway })));
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save lineup');
    } finally {
      setSavingLineup(false);
    }
  };

  // Independent of the lineup itself — stays editable even after a match has started or finished,
  // since an organiser may only realise afterward that a short-handed team played someone twice.
  // Only the given side's own stats are excluded, never their opponent's. If the match hasn't
  // been created yet (lineup not saved), just track it locally; it'll be included in the pairs
  // payload the next time the lineup is saved.
  const handleToggleExclude = async (index: number, side: 'home' | 'away', checked: boolean) => {
    const field = side === 'home' ? 'excludeHome' : 'excludeAway';
    setPairs(prev => prev.map((p, idx) => idx === index ? { ...p, [field]: checked } : p));
    const fm = fixture?.frameMatches[index];
    if (!fm) return;
    try {
      const updated = await setMatchExcludeFromStats(leagueId, divisionId, fixtureId, fm._id, side, checked);
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
      setPairs(prev => prev.map((p, idx) => idx === index ? { ...p, [field]: !checked } : p));
    }
  };

  const handleAddPlayer = async () => {
    if (!addingPlayerFor || !newPlayerName.trim() || !homeTeam || !awayTeam) return;
    const { index, side } = addingPlayerFor;
    const teamId = side === 'home' ? homeTeam._id : awayTeam._id;
    const handicap = newPlayerHandicap.trim() ? Number(newPlayerHandicap) : undefined;
    if (handicap !== undefined && (Number.isNaN(handicap) || handicap < 0)) {
      setError('Handicap must be a non-negative number'); return;
    }
    setSavingNewPlayer(true); setError('');
    try {
      const updated = await addRosterPlayer(leagueId, divisionId, teamId, { name: newPlayerName.trim(), handicap });
      setLeague(updated);
      const updatedTeam = updated.divisions.find(d => d._id === divisionId)?.teams.find(t => t._id === teamId);
      const newPlayer = updatedTeam?.roster[updatedTeam.roster.length - 1];
      if (newPlayer) {
        setPairs(prev => prev.map((p, idx) => idx === index ? { ...p, [side]: newPlayer._id } : p));
      }
      setAddingPlayerFor(null); setNewPlayerName(''); setNewPlayerHandicap('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add player');
    } finally {
      setSavingNewPlayer(false);
    }
  };

  const openQuickScore = (fmId: string) => {
    setQuickScoreFor(fmId);
    setQScore1(''); setQScore2(''); setQHighBreak1(''); setQHighBreak2('');
  };

  const handleQuickScore = async (fmId: string, alreadyStarted: boolean) => {
    const s1 = Number(qScore1); const s2 = Number(qScore2);
    if (!qScore1 || !qScore2 || Number.isNaN(s1) || Number.isNaN(s2) || s1 < 0 || s2 < 0 || s1 === s2) {
      setError('Enter two different, non-negative scores'); return;
    }
    const hb1 = qHighBreak1 ? Number(qHighBreak1) : undefined;
    const hb2 = qHighBreak2 ? Number(qHighBreak2) : undefined;
    if ((hb1 !== undefined && (Number.isNaN(hb1) || hb1 < 0)) || (hb2 !== undefined && (Number.isNaN(hb2) || hb2 < 0))) {
      setError('High break must be a non-negative number'); return;
    }
    setSavingQuickScore(true); setError('');
    try {
      await quickScoreMatch(fmId, alreadyStarted, { score1: s1, score2: s2, highBreak1: hb1, highBreak2: hb2 });
      setQuickScoreFor(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save score');
    } finally {
      setSavingQuickScore(false);
    }
  };

  const handleSaveResult = async () => {
    const h = Number(homeResult); const a = Number(awayResult);
    if (Number.isNaN(h) || Number.isNaN(a) || h < 0 || a < 0) return;
    setSavingResult(true); setError('');
    try {
      const updated = await setFixtureResult(leagueId, divisionId, fixtureId, { homeFrameWins: h, awayFrameWins: a });
      setLeague(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save result');
    } finally {
      setSavingResult(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this fixture?')) return;
    try {
      await deleteFixture(leagueId, divisionId, fixtureId);
      onBack();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete fixture');
    }
  };

  if (error && !league) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league || !division || !fixture || !homeTeam || !awayTeam) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>Loading…</div>;

  const inputStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.5rem 0.7rem', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none' };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        ← Fixtures
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.4rem,5vw,1.9rem)', color: 'var(--gold)' }}>
          {homeTeam.name} <span style={{ color: 'var(--chalk-dim)', fontFamily: 'inherit', fontSize: '0.9rem' }}>vs</span> {awayTeam.name}
        </h2>
        <button onClick={handleDelete} style={{ background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
          Delete
        </button>
      </div>

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '1rem' }}>{error}</p>}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date & time</label>
          <input
            type="datetime-local"
            defaultValue={toLocalInputValue(fixture.scheduledAt)}
            onBlur={e => handleScheduleChange('scheduledAt', e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Venue</label>
          <input
            defaultValue={fixture.venue ?? ''}
            onBlur={e => handleScheduleChange('venue', e.target.value)}
            placeholder="e.g. Red Lion, Backroom"
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>
      </div>

      <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem', fontWeight: 600 }}>
        Lineup ({division.framesPerFixture} match{division.framesPerFixture === 1 ? '' : 'es'})
      </h3>

      {lineupLocked && (
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', marginBottom: '0.75rem' }}>
          🔒 A match has started — the lineup can no longer be changed.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
        {pairs.map((pair, i) => {
          const fm = fixture.frameMatches[i];
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '0.5rem 0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', width: '20px' }}>{i + 1}.</span>
                <select
                  value={pair.home}
                  disabled={lineupLocked}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '__add_new__') {
                      setAddingPlayerFor({ index: i, side: 'home' }); setNewPlayerName(''); setNewPlayerHandicap(''); setError('');
                      return;
                    }
                    setPairs(prev => {
                      const isDup = val !== '' && prev.some((p, idx) => idx !== i && p.home === val);
                      return prev.map((p, idx) => idx === i ? { ...p, home: val, excludeHome: isDup ? true : p.excludeHome } : p);
                    });
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">Select {homeTeam.name} player</option>
                  {homeTeam.roster.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                  {!lineupLocked && <option value="__add_new__">+ Add new player…</option>}
                </select>
                <span style={{ color: 'var(--chalk-dim)', fontSize: '0.8rem' }}>vs</span>
                <select
                  value={pair.away}
                  disabled={lineupLocked}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '__add_new__') {
                      setAddingPlayerFor({ index: i, side: 'away' }); setNewPlayerName(''); setNewPlayerHandicap(''); setError('');
                      return;
                    }
                    setPairs(prev => {
                      const isDup = val !== '' && prev.some((p, idx) => idx !== i && p.away === val);
                      return prev.map((p, idx) => idx === i ? { ...p, away: val, excludeAway: isDup ? true : p.excludeAway } : p);
                    });
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">Select {awayTeam.name} player</option>
                  {awayTeam.roster.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                  {!lineupLocked && <option value="__add_new__">+ Add new player…</option>}
                </select>
                {fm && (
                  fm.status === 'complete' ? (
                    <button onClick={() => onOpenFrame(fm._id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '0.7rem', color: 'var(--gold)', whiteSpace: 'nowrap', minWidth: '70px', textAlign: 'right', fontWeight: 600 }}>
                      {fm.score1}–{fm.score2}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => onOpenFrame(fm._id)}
                        style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '6px', padding: '0.3rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {fm.status === 'active' ? '▶ Continue' : '▶ Score'}
                      </button>
                      <button
                        onClick={() => quickScoreFor === fm._id ? setQuickScoreFor(null) : openQuickScore(fm._id)}
                        style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '0.3rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        ✎ Quick Score
                      </button>
                    </>
                  )
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginLeft: '26px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', color: pair.excludeHome ? 'var(--gold)' : 'var(--chalk-dim)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={pair.excludeHome} onChange={e => handleToggleExclude(i, 'home', e.target.checked)} />
                  {homeTeam.roster.find(p => p._id === pair.home)?.name ?? 'Home player'} playing twice — exclude their stats
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', color: pair.excludeAway ? 'var(--gold)' : 'var(--chalk-dim)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={pair.excludeAway} onChange={e => handleToggleExclude(i, 'away', e.target.checked)} />
                  {awayTeam.roster.find(p => p._id === pair.away)?.name ?? 'Away player'} playing twice — exclude their stats
                </label>
              </div>

              {addingPlayerFor?.index === i && (
                <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.6rem', marginLeft: '26px' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)', marginBottom: '0.4rem' }}>
                    Add new player to {addingPlayerFor.side === 'home' ? homeTeam.name : awayTeam.name}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>Name</label>
                      <input value={newPlayerName} onChange={e => setNewPlayerName(e.target.value)} placeholder="Player name" style={{ ...inputStyle, width: '160px' }} autoFocus />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>Handicap</label>
                      <input type="number" min={0} value={newPlayerHandicap} onChange={e => setNewPlayerHandicap(e.target.value)} placeholder="0" style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                    </div>
                    <button
                      onClick={handleAddPlayer}
                      disabled={savingNewPlayer || !newPlayerName.trim()}
                      style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                      {savingNewPlayer ? '…' : 'Add'}
                    </button>
                    <button onClick={() => setAddingPlayerFor(null)} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.75rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {fm && quickScoreFor === fm._id && (
                <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.6rem', marginLeft: '26px' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                      <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>{homeTeam.roster.find(p => p._id === pair.home)?.name ?? 'Home'} score</label>
                      <input type="number" min={0} value={qScore1} onChange={e => setQScore1(e.target.value)} style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>{awayTeam.roster.find(p => p._id === pair.away)?.name ?? 'Away'} score</label>
                      <input type="number" min={0} value={qScore2} onChange={e => setQScore2(e.target.value)} style={{ ...inputStyle, width: '70px', textAlign: 'center' }} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>High break (home)</label>
                      <input type="number" min={0} value={qHighBreak1} onChange={e => setQHighBreak1(e.target.value)} placeholder="optional" style={{ ...inputStyle, width: '90px', textAlign: 'center' }} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <label style={{ display: 'block', fontSize: '0.65rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>High break (away)</label>
                      <input type="number" min={0} value={qHighBreak2} onChange={e => setQHighBreak2(e.target.value)} placeholder="optional" style={{ ...inputStyle, width: '90px', textAlign: 'center' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button
                      onClick={() => handleQuickScore(fm._id, fm.status === 'active')}
                      disabled={savingQuickScore || !pair.home || !pair.away}
                      style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                    >
                      {savingQuickScore ? '…' : 'Save Score'}
                    </button>
                    <button onClick={() => setQuickScoreFor(null)} style={{ background: 'transparent', color: 'var(--chalk-dim)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontSize: '0.75rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!lineupLocked && (
        <button
          onClick={handleSaveLineup}
          disabled={!lineupComplete || savingLineup}
          style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)', border: 'none', borderRadius: '8px', padding: '0.6rem 1.1rem', fontSize: '0.85rem', fontWeight: 700, cursor: lineupComplete ? 'pointer' : 'not-allowed', opacity: lineupComplete ? 1 : 0.5, marginBottom: '2rem' }}
        >
          {savingLineup ? '…' : fixture.frameMatches.length > 0 ? 'Update Lineup' : 'Set Lineup'}
        </button>
      )}

      <h3 style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: lineupLocked ? '1rem' : 0, marginBottom: '0.75rem', fontWeight: 600 }}>
        Manual Result Override
      </h3>
      <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', marginBottom: '0.75rem' }}>
        For a walkover or correcting a result — sets the fixture's outcome directly without requiring every match to be scored.
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <input type="number" min={0} value={homeResult} onChange={e => setHomeResult(e.target.value)} placeholder={homeTeam.name} style={{ ...inputStyle, width: '80px', textAlign: 'center' }} />
        <span style={{ color: 'var(--chalk-dim)' }}>–</span>
        <input type="number" min={0} value={awayResult} onChange={e => setAwayResult(e.target.value)} placeholder={awayTeam.name} style={{ ...inputStyle, width: '80px', textAlign: 'center' }} />
        <button
          onClick={handleSaveResult}
          disabled={savingResult || homeResult === '' || awayResult === ''}
          style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '8px', padding: '0.5rem 1rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
        >
          {savingResult ? '…' : 'Save Result'}
        </button>
      </div>
      {fixture.status === 'complete' && (
        <p style={{ color: 'var(--gold)', fontSize: '0.8rem', marginTop: '0.75rem', fontWeight: 600 }}>
          Result: {fixture.homeFrameWins}–{fixture.awayFrameWins} {fixture.winnerTeamId ? `(${fixture.winnerTeamId === homeTeam._id ? homeTeam.name : awayTeam.name} win)` : '(draw)'}
        </p>
      )}
    </div>
  );
}
